import { isEqual, without } from 'es-toolkit';
import React, { MouseEvent, PropsWithChildren, useCallback, useMemo } from 'react';
import { Stack } from '@mui/material';
import { createSelector } from 'reselect';

import {
  CeilingPlane,
  ComponentDesign,
  GroupedProductionComponentInstance,
  RoofPlane,
  TrussGroup,
} from '@paragon/contract-library';
import { DataRow, isContainerRow, isDataRow, LoadGroup, Row } from '@paragon/layout-models';
import { getModifierKeys } from '@paragon/utilities';

import { getSelectedItemIdentifiers } from '../../../../library/selection/selectors';
import { isConsideredDesignItem, isSelectableItemType, SelectableItem } from '../../../../library/selection/types';
import { RootState } from '../../../../store';
import { requestContextMenu } from '../../../../store/actions/contextMenuActions';
import { highlightUnknownTypePlaneByGuid } from '../../../../store/actions/layout/genericPlaneActions';
import { handleStandardSceneItemRightClick } from '../../../../store/actions/sceneInteractionActions';
import { selectItem } from '../../../../store/actions/selectActions';
import {
  getActiveComponentDesign,
  getActiveComponentDesignGuid,
} from '../../../../store/selectors/getActiveComponentDesignSelector';
import { getActiveStation } from '../../../../store/selectors/getActiveStation';
import { getProductionComponentInstanceByGuid } from '../../../../store/selectors/getProductionComponentInstanceByGuid';
import { getNavigationPanelSelectedTab } from '../../../../store/selectors/navigationPanelSelectors';
import { getGroupedProductionComponentInstancesForActiveScheduleLineGroup } from '../../../../store/selectors/scheduleStationSelectors';
import { layoutSlice } from '../../../../store/slices/layoutSlice';
import {
  setHoveredComponentDesignGuidInList,
  setHoveredTrussGroupGuidInList,
} from '../../../../store/slices/selectionsSlice';
import { isRightClickableItemContainerType, isRightClickableItemType } from '../../../../store/slices/viewportSlice';
import { setVisibleLoads } from '../../../../store/slices/visualOptionsSlice';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { isComponentDesignRow } from './schemas/CustomTrussDesignSchema';
import { useShiftSelect } from './ShiftSelectContext';

interface Props extends PropsWithChildren {
  row: Row;
}

export function NavigationPanelRow(props: Props) {
  switch (props.row.type) {
    case 'featurePatternTrussDesign':
    case 'profileTrussDesign':
      return <ComponentDesignNavigationPanelRow {...props} row={props.row as DataRow<ComponentDesign>} />;

    case 'groupedProductionComponentInstance':
      return (
        <GroupedProductionComponentInstanceNavigationPanelRow
          {...props}
          row={props.row as DataRow<GroupedProductionComponentInstance>}
        />
      );

    case 'loadGroup':
      return <LoadGroupNavigationPanelRow {...props} row={props.row as DataRow<LoadGroup>} />;

    case 'roofPlane':
    case 'ceilingPlane':
      return <PlaneNavigationPanelRow {...props} row={props.row as DataRow<RoofPlane> | DataRow<CeilingPlane>} />;

    case 'trussGroup':
      return <TrussGroupNavigationPanelRow {...props} row={props.row as DataRow<TrussGroup>} />;

    default:
      return <GenericNavigationPanelRow {...props} />;
  }
}

const shouldComponentDesignRowAppearSelected = createSelector(
  getSelectedItemIdentifiers,
  getNavigationPanelSelectedTab,
  getActiveComponentDesignGuid,
  (state: RootState, rowId: string) => rowId,
  (selectedItemIdentifiers, navigationPanelTab, activeComponentDesignGuid, rowId) =>
    selectedItemIdentifiers.some((item) => item.guid === rowId) ||
    // The active component design appears selected in the explorer panel to indicate that it is active. - Holy Tradition
    (navigationPanelTab === 'component' && activeComponentDesignGuid === rowId),
);

interface ComponentDesignProps extends Props {
  row: DataRow<ComponentDesign>;
}

function ComponentDesignNavigationPanelRow(props: ComponentDesignProps) {
  const dispatch = useAppDispatch();
  const onContextMenu = useContextMenuHandler(props.row);
  const shiftSelect = useShiftSelect();

  const selected = useAppSelector((state) => shouldComponentDesignRowAppearSelected(state, props.row.id));

  const onClick = useCallback(
    (event: MouseEvent) => {
      if (shiftSelect?.handleRowClick(event, props.row.id, 'componentDesign')) return;

      dispatch(
        selectItem(props.row.item, 'componentDesign', { intent: 'toggle', exclusive: !getModifierKeys(event).ctrlKey }),
      );

      shiftSelect?.setAnchor(props.row.id, 'componentDesign');
    },
    [dispatch, props.row.item, props.row.id, shiftSelect],
  );

  const onPointerEnter = useCallback(() => {
    dispatch(setHoveredComponentDesignGuidInList(props.row.item.guid));
  }, [dispatch, props.row.item.guid]);

  const onPointerLeave = useCallback(() => {
    dispatch(setHoveredComponentDesignGuidInList(null));
  }, [dispatch]);

  return (
    <NavigationPanelRowBase
      {...props}
      selected={selected}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onClick={onClick}
      onContextMenu={onContextMenu}
    />
  );
}

interface GroupedProductionComponentInstanceProps extends Props {
  row: DataRow<GroupedProductionComponentInstance>;
}

function GroupedProductionComponentInstanceNavigationPanelRow(props: GroupedProductionComponentInstanceProps) {
  const { row } = props;

  const dispatch = useAppDispatch();
  const onContextMenu = useContextMenuHandler(row);
  const shiftSelect = useShiftSelect();

  const activeStation = useAppSelector(getActiveStation);
  const selectedItems = useAppSelector(getSelectedItemIdentifiers);
  const groupedInstances = useAppSelector(getGroupedProductionComponentInstancesForActiveScheduleLineGroup);
  const productionComponentInstanceByGuid = useAppSelector(getProductionComponentInstanceByGuid);

  const groupForRow = useMemo(
    () => groupedInstances?.find((instances) => instances.guid === row.id) ?? null,
    [groupedInstances, row],
  );
  const firstInstanceForGroup = useMemo(
    () => (groupForRow ? productionComponentInstanceByGuid[groupForRow.productionComponentInstanceGuids[0]] : null),
    [productionComponentInstanceByGuid, groupForRow],
  );

  const isCurrentInstance = useMemo(() => {
    if (
      groupedInstances === null ||
      activeStation === null ||
      !activeStation.followsSchedule ||
      activeStation.currentProductionComponentInstanceGuid === null
    ) {
      return false;
    }

    if (!groupForRow) return false;

    return groupForRow.productionComponentInstanceGuids.some((instanceGuid) =>
      selectedItems.some((selectedItem) => selectedItem.guid === instanceGuid),
    );
  }, [activeStation, groupedInstances, groupForRow, selectedItems]);

  return (
    <NavigationPanelRowBase
      {...props}
      selected={isCurrentInstance && selectedItems.every((item) => isConsideredDesignItem(item.type))}
      onClick={(event) => {
        if (shiftSelect?.handleRowClick(event, row.id, row.type)) return;

        void dispatch(
          selectItem(firstInstanceForGroup, 'productionComponentInstance', {
            intent: 'toggle',
            exclusive: !getModifierKeys(event).ctrlKey,
          }),
        );

        shiftSelect?.setAnchor(row.id, row.type);
      }}
      onContextMenu={onContextMenu}
    />
  );
}

interface LoadGroupProps extends Props {
  row: DataRow<LoadGroup>;
}

function LoadGroupNavigationPanelRow(props: LoadGroupProps) {
  const loadGroup = props.row.item;

  const dispatch = useAppDispatch();
  const onContextMenu = useContextMenuHandler(props.row);

  const selectedItems = useAppSelector(getSelectedItemIdentifiers);
  const activeComponentDesign = useAppSelector(getActiveComponentDesign);
  const visibleLoadGuids = useAppSelector((state) => state.visualOptions.visibleLoadGuids);

  const selected =
    selectedItems.length === 1 &&
    selectedItems[0].type === 'componentDesign' &&
    loadGroup.loads.every((load) => visibleLoadGuids[load.guid] === true);

  return (
    <NavigationPanelRowBase
      {...props}
      selected={selected}
      onClick={(event) => {
        if (loadGroup.loads.length === 0) return;

        const multiSelect = getModifierKeys(event).ctrlKey;

        const currentlyVisibleLoadGuids = Object.keys(visibleLoadGuids);
        const loadGroupLoadGuids = loadGroup.loads.map((load) => load.guid);

        // special case for "deselecting"
        if (selected) {
          // ctrl+unclicking a "selected" load group should deselect all loads in the group but leave others visible
          if (multiSelect && loadGroupLoadGuids.every((loadGuid) => currentlyVisibleLoadGuids.includes(loadGuid))) {
            dispatch(setVisibleLoads(without(currentlyVisibleLoadGuids, ...loadGroupLoadGuids)));
            return;
          }

          // normal clicking the only "selected" load group should "deselect" it
          if (isEqual(currentlyVisibleLoadGuids, loadGroupLoadGuids)) {
            dispatch(setVisibleLoads([]));
            return;
          }
        }

        if (
          activeComponentDesign == null ||
          selectedItems.length !== 1 ||
          selectedItems[0].guid !== activeComponentDesign.guid
        ) {
          dispatch(
            selectItem(activeComponentDesign, 'componentDesign', {
              intent: 'toggle',
              exclusive: !getModifierKeys(event).ctrlKey,
            }),
          );
        }

        const newVisibleLoadGuids = multiSelect
          ? currentlyVisibleLoadGuids.concat(loadGroupLoadGuids)
          : loadGroupLoadGuids;

        dispatch(setVisibleLoads(newVisibleLoadGuids));
      }}
      onContextMenu={onContextMenu}
    />
  );
}

interface PlaneProps extends Props {
  row: DataRow<RoofPlane> | DataRow<CeilingPlane>;
}

function PlaneNavigationPanelRow(props: PlaneProps) {
  const dispatch = useAppDispatch();

  const onPointerEnter = useCallback(() => {
    dispatch(highlightUnknownTypePlaneByGuid(props.row.item.guid));
  }, [dispatch, props.row.item.guid]);

  const onPointerLeave = useCallback(() => {
    dispatch(layoutSlice.actions.highlightedPlanesCleared());
  }, [dispatch]);

  return <GenericNavigationPanelRow {...props} onPointerEnter={onPointerEnter} onPointerLeave={onPointerLeave} />;
}

interface TrussGroupProps extends Props {
  row: DataRow<TrussGroup>;
}

function TrussGroupNavigationPanelRow(props: TrussGroupProps) {
  const dispatch = useAppDispatch();

  const onPointerEnter = useCallback(() => {
    dispatch(setHoveredTrussGroupGuidInList(props.row.item.guid));
  }, [dispatch, props.row.item.guid]);

  const onPointerLeave = useCallback(() => {
    dispatch(setHoveredTrussGroupGuidInList(null));
  }, [dispatch]);

  return <GenericNavigationPanelRow {...props} onPointerEnter={onPointerEnter} onPointerLeave={onPointerLeave} />;
}

function GenericNavigationPanelRow(props: Props & Partial<NavigationPanelRowBaseProps>) {
  const dispatch = useAppDispatch();
  const onContextMenu = useContextMenuHandler(props.row);
  const shiftSelect = useShiftSelect();

  const { row } = props;

  const isSelected = useAppSelector(
    (state) => isDataRow(row) && getSelectedItemIdentifiers(state).some((selectedItem) => row.id === selectedItem.guid),
  );

  return (
    <NavigationPanelRowBase
      selected={isSelected}
      onClick={(event: MouseEvent) => {
        if (!isDataRow(row)) return;
        if (row.id === 'nullAnalogModel') return;

        const selectableType = isSelectableItemType(row.type) ? row.type : null;
        if (selectableType == null) return;

        if (shiftSelect?.handleRowClick(event, row.id, selectableType)) return;

        dispatch(
          selectItem(row.item as SelectableItem, selectableType, {
            intent: 'toggle',
            exclusive: !getModifierKeys(event).ctrlKey,
          }),
        );

        shiftSelect?.setAnchor(row.id, selectableType);
      }}
      onContextMenu={onContextMenu}
      {...props}
    />
  );
}

export function useContextMenuHandler(row: Row) {
  const dispatch = useAppDispatch();

  return useCallback(
    async (event: MouseEvent) => {
      event.preventDefault();

      if (isContainerRow(row)) {
        const type = `${row.childrenType}Container`;

        if (isRightClickableItemContainerType(type)) {
          dispatch(requestContextMenu(event, { type }, { outsideScene: true }));
        }
      } else if (isDataRow(row)) {
        const type = isComponentDesignRow(row) ? 'componentDesign' : row.type;

        if (isRightClickableItemType(type) && type !== 'referenceLine') {
          await dispatch(
            handleStandardSceneItemRightClick(event, {
              type,
              guid: (row.item as { guid: string }).guid,
              origin: 'dom',
            }),
          );
        }
      }
    },
    [dispatch, row],
  );
}

type NavigationPanelRowBaseProps = Props & {
  selected: boolean;

  onClick: (event: MouseEvent) => void;
  onContextMenu: (event: MouseEvent) => void;
  onPointerEnter?: (event: MouseEvent) => void;
  onPointerLeave?: (event: MouseEvent) => void;
};

export function getDataRowHtmlId(row: DataRow<unknown>) {
  return `navigation-panel-row-${row.id}`;
}

/**
 * The presentational component for a row in the navigation panel.
 */
function NavigationPanelRowBase(props: NavigationPanelRowBaseProps) {
  return (
    <Stack
      id={isDataRow(props.row) ? getDataRowHtmlId(props.row) : undefined}
      direction="row"
      sx={[
        { alignItems: 'center' },
        props.selected ? { bgcolor: 'selectedList.main' } : { '&:hover': { bgcolor: 'action.hover' } },
      ]}
      onClick={props.onClick}
      onContextMenu={props.onContextMenu}
      onPointerEnter={props.onPointerEnter}
      onPointerLeave={props.onPointerLeave}
    >
      {props.children}
    </Stack>
  );
}
